import React from "react";
import dayjs from "utils/dayjs";
import Box from "@mui/material/Box";
import type { ChipTone, TimelineStep } from "@outpave/ds";
import { sentenceCaseCode } from "./eventLogsUtils";

export type WebhookEndpointStatus = "ACTIVE" | "PAUSED" | "DELETED";
export type WebhookDeliveryStatus =
  "PENDING" | "SUCCEEDED" | "FAILED" | "CANCELED";
export type WebhookDeliveryTrigger = "EVENT" | "REPLAY" | "TEST";

export type PlatformWebhookEndpoint = {
  id: string;
  url: string;
  status: WebhookEndpointStatus;
};

export type WebhookDeliveryAttempt = {
  attemptNumber: number;
  startedAt: string;
  durationMs: number | null;
  outcome: string;
  statusCode: number | null;
  responseExcerpt: string | null;
  errorCode: string | null;
};

export type WebhookDelivery = {
  id: string;
  webhookEndpointId: string;
  eventId: string;
  eventType: string | null;
  trigger: WebhookDeliveryTrigger;
  status: WebhookDeliveryStatus;
  statusReason: string | null;
  attemptCount: number;
  nextAttemptAt: string | null;
  completedAt: string | null;
  createdAt: string;
  attempts: WebhookDeliveryAttempt[];
};

export type PlatformWebhookDeliveriesQueryData = {
  platformWebhookDeliveries?: { edges?: { node: WebhookDelivery }[] };
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const DELIVERY_STATUS_LABEL: Record<WebhookDeliveryStatus, string> = {
  PENDING: "Pending",
  SUCCEEDED: "Succeeded",
  FAILED: "Failed",
  CANCELED: "Canceled",
};

export const DELIVERY_STATUS_TONE: Record<WebhookDeliveryStatus, ChipTone> = {
  PENDING: "info",
  SUCCEEDED: "positive",
  FAILED: "negative",
  CANCELED: "neutral",
};

export const DELIVERY_TRIGGER_LABEL: Record<WebhookDeliveryTrigger, string> = {
  EVENT: "Event",
  REPLAY: "Replay",
  TEST: "Test",
};

const STATUS_REASON_LABEL: Record<string, string> = {
  ATTEMPTS_EXHAUSTED: "Every attempt failed",
  DEADLINE_PASSED: "Retry window ended",
  EVENT_EXPIRED: "Event expired",
  ENDPOINT_PAUSED: "Endpoint paused",
  ENDPOINT_DELETED: "Endpoint deleted",
  NOT_SUBSCRIBED: "Endpoint not subscribed to this event",
  NOT_PERMITTED: "Event type not permitted for this endpoint",
  NOT_ELIGIBLE: "Endpoint cannot receive this event",
};

const ATTEMPT_OUTCOME_LABEL: Record<string, string> = {
  SUCCEEDED: "Delivered",
  HTTP_ERROR: "Error response",
  REDIRECTED: "Redirected",
  TIMED_OUT: "Timed out",
  CONNECTION_FAILED: "Connection failed",
  DESTINATION_REFUSED: "Destination refused",
  DESTINATION_UNAVAILABLE: "Destination unavailable",
  INTERRUPTED: "Interrupted",
};

// A receiver's reply can be one unbroken token, which must wrap in the drawer.
const excerptSx = { display: "block", wordBreak: "break-word" } as const;

export const extractWebhookDeliveries = (
  data: PlatformWebhookDeliveriesQueryData,
): WebhookDelivery[] =>
  data?.platformWebhookDeliveries?.edges?.map((edge) => edge.node) ?? [];

// A test delivery's event exists only on the delivery, so there is nothing to replay.
export const isReplayable = (delivery: WebhookDelivery): boolean =>
  delivery.trigger !== "TEST";

export const formatInstant = (instant: string | null): string =>
  instant ? dayjs(instant).format("MMM D, YYYY h:mm:ss A") : "-";

export const getEndpointLabel = (
  endpoint: PlatformWebhookEndpoint,
  t: Translate,
): string =>
  endpoint.status === "PAUSED"
    ? `${endpoint.url} (${t("Paused")})`
    : endpoint.url;

// A code added on the server after this client shipped still reads as words.
const getCodeLabel = (
  labels: Record<string, string>,
  code: string,
  t: Translate,
): string => (labels[code] ? t(labels[code]) : sentenceCaseCode(code));

export const getStatusReasonLabel = (reason: string, t: Translate): string =>
  getCodeLabel(STATUS_REASON_LABEL, reason, t);

export const getAttemptSummary = (
  attempt: WebhookDeliveryAttempt,
  t: Translate,
): string =>
  [
    getCodeLabel(ATTEMPT_OUTCOME_LABEL, attempt.outcome, t),
    attempt.statusCode !== null &&
      t("HttpStatusCode", { code: attempt.statusCode }),
    attempt.durationMs !== null &&
      t("DurationMs", { duration: attempt.durationMs }),
    attempt.errorCode,
  ]
    .filter(Boolean)
    .join(", ");

// Oldest attempt first, then the next one while the delivery is waiting.
export const getAttemptSteps = (
  delivery: WebhookDelivery,
  t: Translate,
): TimelineStep[] => [
  ...[...delivery.attempts].reverse().map((attempt): TimelineStep => ({
    key: `attempt-${attempt.attemptNumber}`,
    label: t("AttemptNumber", { count: attempt.attemptNumber }),
    hint: (
      <>
        <span>{getAttemptSummary(attempt, t)}</span>
        {attempt.responseExcerpt && (
          <Box component="span" sx={excerptSx}>
            {attempt.responseExcerpt}
          </Box>
        )}
      </>
    ),
    trailing: formatInstant(attempt.startedAt),
    state: attempt.outcome === "SUCCEEDED" ? "done" : "incident",
  })),
  ...(delivery.nextAttemptAt
    ? [
        {
          key: "next-attempt",
          label: t("Next attempt"),
          trailing: formatInstant(delivery.nextAttemptAt),
          state: "upcoming" as const,
        },
      ]
    : []),
];
